import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as StripePayoutsIcon } from '../../../assets/icons/sidebar/stripe-payout.svg';
import { useAppBranding } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  RequestStripePayoutsInterface,
  StripePayoutsInterface,
} from '../../../interface/stripe-payouts.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  fetchApprovedStripePayoutsMethodFilters,
  fetchStripePayouts,
  updateApprovedStripePayoutsFilters,
  disableApprovedStripePayoutsApplied,
  setSortData,
} from '../../../redux/slices/ApprovedStripePayoutsSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import capitalizeFirstLetter from '../../../shared-functions/StringFunctions';
import ToFixedValue from '../../../shared-functions/ToFixedValue';

/**
 * Approved stripe payouts components
 * @returns
 */
function ApprovedStripePayouts() {
  const branding = useAppBranding();
  const { t, i18n } = useTranslation('stripePayout');
  const {
    payouts,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.stripePayouts);
  const dispatch = useAppDispatch();

  /**
   * Handle the fetch the data in api function
   * @param data
   */
  const fetchData = (data: RequestStripePayoutsInterface) => {
    if (!loading) {
      dispatch(fetchStripePayouts(data));
    }
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: RequestStripePayoutsInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  const column = [
    {
      label: t('accountID'),
      accessorKey: 'accountCustomId' as keyof StripePayoutsInterface,
      sortAllow: false,
    },
    {
      label: t('accountName'),
      accessorKey: 'accountName' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('email'),
      accessorKey: 'email' as keyof StripePayoutsInterface,
      sortAllow: false,
    },
    {
      label: t('revenueFromDate'),
      accessorKey: 'fromDate' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('revenueToDate'),
      accessorKey: 'toDate' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('payoutID'),
      accessorKey: 'transferId' as keyof StripePayoutsInterface,
      sortAllow: false,
    },
    {
      label: t('payoutAmount'),
      accessorKey: 'amount' as keyof StripePayoutsInterface,
      sortAllow: true,
      /**
       * Payment amount view in table
       * @param payouts
       * @returns
       */
      customData: (payoutsStatus: StripePayoutsInterface) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(payoutsStatus.amount)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof StripePayoutsInterface,
      sortAllow: false,
      /**
       * Payment status view in table
       * @param payouts
       * @returns
       */
      customData: (payoutsStatus: StripePayoutsInterface) => (
        <div className="status-in-stripe-payout-table">
          <p className={`${payoutsStatus.status === 'transferred' ? 'success-text' : null}`}>
            {capitalizeFirstLetter(payoutsStatus.status)}
          </p>
        </div>
      ),
    },
    {
      label: t('payoutDate'),
      accessorKey: 'transferredAt' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
  ];
  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    if (isFilterUpdated || payouts.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableApprovedStripePayoutsApplied());
    }
  }, [filters]);
  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('stripePayouts'),
        icon: <StripePayoutsIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults, t]);

  return (
    <div className="table-container" dir={i18n.dir()}>
      <CustomTable
        header={column}
        data={payouts}
        version="v2"
        serialNo
        limit={limit}
        page={page}
        loading={loading}
        totalPages={totalPages}
        height={window.innerHeight >= 700 ? 327 : 342}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updateApprovedStripePayoutsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchApprovedStripePayoutsMethodFilters}
        refreshRequired
        ReferenceCurrentPages={handleRefresh}
        handleRefresh={handleRefresh}
        hoverLabel={t('refresh')}
      />
    </div>
  );
}

export default ApprovedStripePayouts;
